// CONEK-8802 — the swipe pager's local drafts (offline continuity).
//
// The pager keeps every page's answers in the document, but a document does
// not survive a reload, the app being killed, or the inspector closing the tab
// while the connection is down. So each page's form is also written here, to
// IndexedDB, as the inspector answers it, and removed once the server has
// confirmed a save of it. On the next load the pager puts back whatever is
// still here and sends it.
//
// One record per (user, inspection, page): the user is part of the key so a
// shared device never hands one inspector's drafts to another. A record holds
// the form's answer fields, whether the page was left (it must be completed
// when sent), the screen the answers were given on (CONEK-8857: the form's
// `answers_token`, `answers_revision` and `answers_seq` — a draft is sent
// again as a replay of that screen, so the server never takes it over newer
// answers), and a revision that grows on every write, so a save confirmed for
// an older revision never deletes a newer draft. Drafts untouched for a month
// are purged.
//
// A small wrapper rather than Dexie: Dexie is only in the React canvas bundle,
// and this needs four operations. Every failure degrades to "no drafts" — the
// pager keeps working as it does without this; it never blocks on storage.

const DB_NAME = "conek-inspections"
const DB_VERSION = 1
const STORE = "page-drafts"
const BY_INSPECTION = "by-inspection"

const request = (req) =>
  new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })

export class DraftStore {
  // A store, or null when IndexedDB is unavailable (private browsing on some
  // browsers, storage blocked) or cannot be opened.
  static async open() {
    if (typeof indexedDB === "undefined") return null
    try {
      const open = indexedDB.open(DB_NAME, DB_VERSION)
      open.onupgradeneeded = () => {
        const store = open.result.createObjectStore(STORE, {
          keyPath: ["userId", "inspectionId", "pageId"],
        })
        store.createIndex(BY_INSPECTION, ["userId", "inspectionId"])
      }
      return new DraftStore(await request(open))
    } catch {
      return null
    }
  }

  constructor(db) {
    this.db = db
  }

  close() {
    this.db.close()
  }

  async put(record) {
    try {
      await request(this.store("readwrite").put(record))
      return true
    } catch {
      return false
    }
  }

  async get(userId, inspectionId, pageId) {
    try {
      return (
        (await request(
          this.store("readonly").get([userId, inspectionId, pageId]),
        )) || null
      )
    } catch {
      return null
    }
  }

  async forInspection(userId, inspectionId) {
    try {
      return await request(
        this.store("readonly")
          .index(BY_INSPECTION)
          .getAll([userId, inspectionId]),
      )
    } catch {
      return []
    }
  }

  // Removes the draft only if nothing newer than `upToRev` was written since:
  // the confirmed save carried that revision, a later edit has not been sent.
  async deleteUpTo(userId, inspectionId, pageId, upToRev) {
    try {
      const store = this.store("readwrite")
      const key = [userId, inspectionId, pageId]
      const current = await request(store.get(key))
      if (current && current.rev <= upToRev) await request(store.delete(key))
      return true
    } catch {
      return false
    }
  }

  // Removes every draft last written before `cutoff` (ms since the epoch),
  // whoever's: an inspection left that long is not coming back to this device.
  async purgeOlderThan(cutoff) {
    try {
      const store = this.store("readwrite")
      const records = await request(store.getAll())
      await Promise.all(
        records
          .filter((record) => record.updatedAt < cutoff)
          .map((record) =>
            request(
              store.delete([record.userId, record.inspectionId, record.pageId]),
            ),
          ),
      )
      return true
    } catch {
      return false
    }
  }

  store(mode) {
    return this.db.transaction(STORE, mode).objectStore(STORE)
  }
}

// The form's answer fields as [name, value] pairs: what is restored into the
// form later. Rails' own fields (token, method), the pager's navigation field
// and the screen fields are left out — they are not answers.
const SCREEN_FIELDS = {
  token: "answers_token",
  revision: "answers_revision",
  seq: "answers_seq",
}
const NOT_ANSWERS = new Set([
  "authenticity_token",
  "_method",
  "direction",
  "target_page_id",
  ...Object.values(SCREEN_FIELDS),
])

// The screen a form's answers belong to, as its hidden fields hold it.
export const readScreen = (form) =>
  Object.fromEntries(
    Object.entries(SCREEN_FIELDS).map(([key, name]) => [
      key,
      form.elements.namedItem(name)?.value ?? "",
    ]),
  )

// Makes `form` the screen `screen` again: its next saves are that screen's.
// A hidden input's value is its attribute, so a copy of the page Hotwire
// Native restores carries it too.
export const writeScreen = (form, screen) => {
  Object.entries(SCREEN_FIELDS).forEach(([key, name]) => {
    const field = form.elements.namedItem(name)
    if (field && screen?.[key] !== undefined) field.value = String(screen[key])
  })
}

export const serializeAnswers = (form) =>
  Array.from(new FormData(form).entries())
    .filter(
      ([name, value]) => !NOT_ANSWERS.has(name) && typeof value === "string",
    )
    .map(([name, value]) => [name, value])

// Puts serialized answers back into `form`, then fires the events the page's
// controllers listen to, so the severity pill, the diagram and the sliders
// follow. Hidden fields are never touched: they are the form's own.
export const restoreAnswers = (form, fields) => {
  const values = new Map()
  fields.forEach(([name, value]) => {
    if (!values.has(name)) values.set(name, [])
    values.get(name).push(value)
  })

  const touched = []
  Array.from(form.elements).forEach((element) => {
    if (!(
      element instanceof HTMLInputElement ||
      element instanceof HTMLTextAreaElement
    ))
      return
    if (
      !element.name ||
      element.type === "hidden" ||
      element.type === "file" ||
      element.type === "submit"
    )
      return
    if (NOT_ANSWERS.has(element.name)) return

    const wanted = values.get(element.name) || []
    if (element.type === "radio" || element.type === "checkbox") {
      const checked = wanted.includes(element.value)
      if (element.checked !== checked) {
        element.checked = checked
        touched.push(element)
      }
    } else if (wanted.length > 0 && element.value !== wanted[0]) {
      element.value = wanted[0]
      touched.push(element)
    }
  })

  // As a tap would: a radio group reports only the option that ends up
  // checked. Announcing the one being unchecked too made the severity engine
  // recompute with neither picked yet and restore the server's choice over
  // the draft's.
  touched
    .filter((element) => element.type !== "radio" || element.checked)
    .forEach((element) => {
      element.dispatchEvent(new Event("input", { bubbles: true }))
      element.dispatchEvent(new Event("change", { bubbles: true }))
    })
  return touched.length
}
